import React from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { 
  Building2,
  Shield, 
  FolderOpen, 
  ScrollText,
  Settings,
  Palette,
  ChevronRight
} from 'lucide-react';
import { Button } from '../../components/ui/button';

/**
 * Organization Workspace
 * 
 * Contains: Compliance, Documents, Policies, Settings, Branding
 */

const ORGANIZATION_SECTIONS = [
  {
    id: 'compliance',
    name: 'Compliance',
    description: 'Compliance tracking and certifications',
    icon: Shield,
    path: '/organization/compliance',
    color: 'bg-blue-500',
    stats: { label: 'Items Due', value: '—' }
  },
  {
    id: 'documents',
    name: 'Documents',
    description: 'Organization documents and files',
    icon: FolderOpen,
    path: '/organization/documents',
    color: 'bg-emerald-500',
    stats: null
  },
  {
    id: 'policies',
    name: 'Policies',
    description: 'Company policies and procedures',
    icon: ScrollText,
    path: '/organization/policies',
    color: 'bg-purple-500',
    stats: null
  },
  {
    id: 'settings',
    name: 'Settings',
    description: 'Organization configuration',
    icon: Settings,
    path: '/organization/settings',
    color: 'bg-gray-500',
    stats: null
  },
  {
    id: 'branding',
    name: 'Branding',
    description: 'Logo, colors, and appearance',
    icon: Palette,
    path: '/organization/branding',
    color: 'bg-pink-500',
    stats: null
  },
];

export default function OrganizationWorkspace() {
  const location = useLocation();
  const isLanding = location.pathname === '/organization';

  // If we're on a sub-route, render the Outlet
  if (!isLanding) {
    return <Outlet />;
  }

  return (
    <div className="min-h-screen bg-[#FBFBFA]">
      <div className="max-w-6xl mx-auto px-6 py-8">
        
        {/* Header */}
        <header className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <h1 className="text-2xl font-semibold text-[#0D1B33]">Organization</h1>
            <Link to="/organization/settings">
              <Button variant="outline" className="border-[#E2E8F0]">
                <Settings className="w-4 h-4 mr-2" />
                Settings
              </Button>
            </Link>
          </div>
          <p className="text-[#64748B]">
            Compliance, documents, policies, and organization settings.
          </p>
        </header>

        {/* Section Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {ORGANIZATION_SECTIONS.map((section) => (
            <Link
              key={section.id}
              to={section.path}
              className="bg-white rounded-xl border border-[#E2E8F0] p-5 hover:border-[#176DFF]/30 hover:shadow-sm transition-all group"
            >
              <div className="flex items-start justify-between mb-4">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-white ${section.color}`}>
                  <section.icon className="w-5 h-5" />
                </div>
                <ChevronRight className="w-5 h-5 text-[#CBD5E1] group-hover:text-[#176DFF] group-hover:translate-x-1 transition-all" />
              </div>
              <h3 className="font-semibold text-[#0D1B33] mb-1">{section.name}</h3>
              <p className="text-sm text-[#64748B] mb-3">{section.description}</p>
              {section.stats && (
                <div className="flex items-center gap-2 text-xs">
                  <span className="text-[#64748B]">{section.stats.label}:</span>
                  <span className="font-semibold text-[#0D1B33]">{section.stats.value}</span>
                </div>
              )}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
